import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { db } from "../firebase";
import { ref, get, update } from "firebase/database";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Star, ShieldCheck, Package, BookOpen } from "lucide-react";
import { canSeePhoto } from "../utils/photoPrivacy";

function formatResponseTime(ms) {
  if (!ms) return null;
  const mins = Math.floor(ms / 60000);
  if (mins < 5) return "Responds instantly";
  if (mins < 60) return `Responds in ~${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Responds in ~${hrs} hr${hrs > 1 ? "s" : ""}`;
  return `Responds in ~${Math.floor(hrs / 24)} day${Math.floor(hrs / 24) > 1 ? "s" : ""}`;
}

function lastActiveBadge(ts) {
  if (!ts) return null;
  const mins = Math.floor((Date.now() - ts) / 60000);
  if (mins < 5) return { text: "Active now", color: "text-green-500" };
  if (mins < 60) return { text: `Active ${mins}m ago`, color: "text-green-500" };
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return { text: `Active ${hrs}h ago`, color: "text-blue-500" };
  const days = Math.floor(hrs / 24);
  if (days < 7) return { text: `Active ${days}d ago`, color: "text-gray-400" };
  return { text: "Inactive", color: "text-gray-400" };
}

export default function SellerProfile() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [seller, setSeller] = useState(null);
  const [listings, setListings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(false);
  const [responseRate, setResponseRate] = useState(null);
  const [canSeeSellerPhoto, setCanSeeSellerPhoto] = useState(true);
  const [soldCount, setSoldCount] = useState(0);

  useEffect(() => {
    if (!uid) return;
    async function load() {
      try {
        const [sellerSnap, listSnap, revSnap] = await Promise.all([
          get(ref(db, `users/${uid}`)),
          get(ref(db, "listings")),
          get(ref(db, `reviews/${uid}`)),
        ]);
        if (sellerSnap.exists()) {
          const sellerData = sellerSnap.val();
          setSeller(sellerData);
          // Check photo privacy
          const visible = await canSeePhoto(uid, sellerData, currentUser?.uid || null);
          setCanSeeSellerPhoto(visible);
        }
        if (listSnap.exists()) {
          const allListings = Object.entries(listSnap.val()).map(([id, d]) => ({ id, ...d }));
          const active = allListings
            .filter(l => l.sellerId === uid && l.status === "active")
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          const sold = allListings.filter(l => l.sellerId === uid && l.status === "sold").length;
          setListings(active);
          setSoldCount(sold);
        }
        if (revSnap.exists()) {
          const revs = Object.entries(revSnap.val())
            .map(([id, r]) => ({ id, ...r }))
            .sort((a, b) => b.createdAt - a.createdAt)
            .slice(0, 5);
          setReviews(revs);
        }
        if (currentUser && currentUser.uid !== uid) {
          const blockSnap = await get(ref(db, `blocked/${currentUser.uid}/${uid}`));
          setBlocked(blockSnap.exists());
        }
        // Calculate seller response rate from chats
        try {
          const chatsSnap = await get(ref(db, "chats"));
          if (chatsSnap.exists()) {
            const sellerChats = Object.entries(chatsSnap.val())
              .filter(([, c]) => c.sellerId === uid)
              .slice(0, 10); // limit to last 10 chats
            const responseTimes = [];
            for (const [cid] of sellerChats) {
              const msgsSnap = await get(ref(db, `chats/${cid}/messages`));
              if (!msgsSnap.exists()) continue;
              const msgs = Object.values(msgsSnap.val()).sort((a, b) => a.createdAt - b.createdAt);
              for (let i = 1; i < msgs.length; i++) {
                if (msgs[i].senderId === uid && msgs[i-1].senderId !== uid) {
                  responseTimes.push(msgs[i].createdAt - msgs[i-1].createdAt);
                  break;
                }
              }
            }
            if (responseTimes.length > 0) {
              const avgMs = responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length;
              setResponseRate(avgMs);
            }
          }
        } catch (e) {}
      } catch (e) {  }
      setLoading(false);
    }
    load();
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  async function toggleBlock() {
    if (!currentUser) return;
    if (blocked) {
      await update(ref(db, `blocked/${currentUser.uid}`), { [uid]: null });
      setBlocked(false);
    } else {
      if (!window.confirm(`Block ${seller?.name}? They won't be able to see you and you won't see their listings.`)) return;
      await update(ref(db, `blocked/${currentUser.uid}`), { [uid]: true });
      setBlocked(true);
    }
  }

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!seller) return (
    <div className="p-8 text-center text-gray-400 pt-20">
      <p className="text-4xl mb-3">👤</p>
      <p className="font-medium text-gray-600">Seller not found</p>
      <button onClick={() => navigate(-1)} className="mt-4 bg-blue-600 text-white px-5 py-2 rounded-xl text-sm font-semibold">Go Back</button>
    </div>
  );

  const initial = seller.name?.[0]?.toUpperCase() || "?";

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-b from-blue-600 to-blue-700 px-4 pt-4 pb-8">
        <button onClick={() => navigate(-1)} className="p-1.5 bg-white/20 rounded-full mb-4">
          <ArrowLeft size={20} className="text-white" />
        </button>
        <div className="flex items-center gap-4">
          <div className="relative">
            {seller.photoURL && canSeeSellerPhoto
              ? <img src={seller.photoURL} alt={seller.name} className="w-16 h-16 rounded-full object-cover border-2 border-white" />
              : <div className="w-16 h-16 rounded-full bg-yellow-400 text-blue-900 flex items-center justify-center font-bold text-2xl border-2 border-white">{initial}</div>}
            {seller.verified && (
              <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-0.5 border border-white">
                <ShieldCheck size={12} className="text-white" />
              </div>
            )}
          </div>
          <div className="text-white">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold">{seller.name}</h1>
              {seller.eduVerified && <span className="text-xs bg-white/20 px-1.5 py-0.5 rounded-full font-semibold">.edu</span>}
            </div>
            <p className="text-blue-200 text-sm">{seller.college || "Campus Student"}</p>
            {seller.totalRatings > 0 && (
              <div className="flex items-center gap-1 mt-1">
                <Star size={13} className="text-yellow-300 fill-yellow-300" />
                <span className="text-sm font-semibold">{seller.rating?.toFixed(1)}</span>
                <span className="text-blue-200 text-xs">({seller.totalRatings} ratings)</span>
              </div>
            )}
            {responseRate !== null && (
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-xs text-green-600 font-medium">⚡ {formatResponseTime(responseRate)}</span>
              </div>
            )}
            {seller.lastActive && (() => {
              const badge = lastActiveBadge(seller.lastActive);
              return <p className={`text-xs mt-0.5 ${badge.color}`}>{badge.text}</p>;
            })()}
          </div>
        </div>

        {/* Stats */}
        <div className="flex gap-2 mt-5">
          <div className="flex-1 bg-white/20 rounded-xl px-2 py-2 text-center">
            <p className="text-white font-bold text-lg">{listings.length}</p>
            <p className="text-blue-200 text-xs">Active</p>
          </div>
          <div className="flex-1 bg-white/20 rounded-xl px-2 py-2 text-center">
            <p className="text-white font-bold text-lg">{soldCount}</p>
            <p className="text-blue-200 text-xs">Sold</p>
          </div>
          <div className="flex-1 bg-white/20 rounded-xl px-2 py-2 text-center">
            <p className="text-white font-bold text-lg">{seller.totalRatings || 0}</p>
            <p className="text-blue-200 text-xs">Reviews</p>
          </div>
          <div className="flex-1 bg-white/20 rounded-xl px-2 py-2 text-center">
            {(() => {
              // Seller score out of 100
              const ratingScore = Math.round(((seller.rating || 0) / 5) * 35);
              const verifiedScore = seller.verified ? 25 : 0;
              const salesScore = soldCount >= 20 ? 25 : soldCount >= 10 ? 18 : soldCount >= 5 ? 12 : soldCount >= 1 ? 6 : 0;
              const responseScore = responseRate
                ? responseRate < 300000 ? 15 : responseRate < 3600000 ? 10 : 5
                : 0;
              const score = Math.min(100, ratingScore + verifiedScore + salesScore + responseScore);
              const color = score >= 75 ? "text-green-300" : score >= 50 ? "text-yellow-300" : "text-orange-300";
              return (
                <>
                  <p className={`font-bold text-lg ${color}`}>{score}</p>
                  <p className="text-blue-200 text-xs">Score</p>
                </>
              );
            })()}
          </div>
        </div>

        {currentUser && currentUser.uid !== uid && (
          <button onClick={toggleBlock}
            className={`mt-3 text-xs font-semibold px-4 py-1.5 rounded-full ${blocked ? "bg-white/20 text-white" : "bg-red-500/80 text-white"}`}>
            {blocked ? "🔓 Unblock User" : "🚫 Block User"}
          </button>
        )}
      </div>

      <div className="px-4 -mt-4 space-y-4 max-w-lg mx-auto">
        {/* Seller Score Card */}
        {(() => {
          const ratingScore = Math.round(((seller.rating || 0) / 5) * 35);
          const verifiedScore = seller.verified ? 25 : 0;
          const salesScore = soldCount >= 20 ? 25 : soldCount >= 10 ? 18 : soldCount >= 5 ? 12 : soldCount >= 1 ? 6 : 0;
          const responseScore = responseRate
            ? responseRate < 300000 ? 15 : responseRate < 3600000 ? 10 : 5
            : 0;
          const score = Math.min(100, ratingScore + verifiedScore + salesScore + responseScore);
          const badge = score >= 75 ? { label: "Top Seller", color: "text-green-600", bg: "bg-green-50", bar: "bg-green-500" }
            : score >= 50 ? { label: "Trusted Seller", color: "text-blue-600", bg: "bg-blue-50", bar: "bg-blue-500" }
            : score >= 25 ? { label: "Active Seller", color: "text-yellow-600", bg: "bg-yellow-50", bar: "bg-yellow-400" }
            : { label: "New Seller", color: "text-gray-500", bg: "bg-gray-50", bar: "bg-gray-300" };
          return (
            <div className="bg-white rounded-2xl shadow-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-bold text-gray-800 text-sm">Seller Score</p>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badge.bg} ${badge.color}`}>{badge.label}</span>
                </div>
                <div className={`text-3xl font-extrabold ${badge.color}`}>{score}<span className="text-sm text-gray-400 font-normal">/100</span></div>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2 mb-3">
                <div className={`h-2 rounded-full transition-all ${badge.bar}`} style={{ width: `${score}%` }} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2">
                  <span className="text-xs text-gray-500">⭐ Rating</span>
                  <span className="text-xs font-bold text-gray-700">{ratingScore}/35</span>
                </div>
                <div className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2">
                  <span className="text-xs text-gray-500">✅ Verified</span>
                  <span className="text-xs font-bold text-gray-700">{verifiedScore}/25</span>
                </div>
                <div className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2">
                  <span className="text-xs text-gray-500">📦 Sales</span>
                  <span className="text-xs font-bold text-gray-700">{salesScore}/25</span>
                </div>
                <div className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2">
                  <span className="text-xs text-gray-500">⚡ Response</span>
                  <span className="text-xs font-bold text-gray-700">{responseScore}/15</span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Active Listings */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-50 flex items-center gap-2">
            <Package size={16} className="text-blue-500" />
            <p className="font-bold text-gray-800 text-sm">Active Listings ({listings.length})</p>
          </div>
          {listings.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-400 text-sm">No active listings</div>
          ) : (
            <div className="grid grid-cols-2 gap-3 p-3">
              {listings.map(l => (
                <Link key={l.id} to={`/listing/${l.id}`} className="bg-gray-50 rounded-xl overflow-hidden border border-gray-100 hover:border-blue-200 transition block">
                  <div className="h-28 bg-gray-100 overflow-hidden">
                    {l.imageUrl
                      ? <img src={l.imageUrl} alt={l.title} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center"><BookOpen size={24} className="text-gray-200" /></div>}
                  </div>
                  <div className="p-2">
                    <p className="text-xs font-semibold text-gray-800 line-clamp-1">{l.title}</p>
                    <p className="text-xs font-bold text-blue-600 mt-0.5">{l.price === 0 ? "Negotiable" : `₹${l.price}`}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Reviews */}
        {reviews.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-50 flex items-center gap-2">
              <Star size={16} className="text-yellow-400 fill-yellow-400" />
              <p className="font-bold text-gray-800 text-sm">Reviews</p>
            </div>
            <div className="divide-y divide-gray-50">
              {reviews.map(r => (
                <div key={r.id} className="px-4 py-3">
                  <div className="flex items-center gap-1 mb-1">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} size={12} className={s <= r.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-200 fill-gray-200"} />
                    ))}
                    <span className="text-xs text-gray-400 ml-1">
                      {new Date(r.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  </div>
                  {r.comment && <p className="text-sm text-gray-600 italic">"{r.comment}"</p>}
                  <p className="text-xs text-gray-400 mt-0.5">for: {r.listingTitle}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
